import type { Locale } from "@/lib/i18n";

const MS_PER_DAY = 86_400_000;
const MS_PER_MINUTE = 60_000;

export type CalendarId = "gregory" | "persian" | "islamic-umalqura";

const CALENDARS: Record<Locale, CalendarId> = {
  en: "gregory",
  fa: "persian",
  ar: "islamic-umalqura",
};

const WEEK_STARTS: Record<Locale, number> = { en: 0, fa: 6, ar: 6 };

export function calendarFor(locale: Locale): CalendarId {
  return CALENDARS[locale];
}

export function weekStartFor(locale: Locale): number {
  return WEEK_STARTS[locale];
}

export function intlTag(locale: Locale): string {
  switch (locale) {
    case "fa":
      return "fa-u-ca-persian";
    case "ar":
      return "ar-u-ca-islamic-umalqura-nu-arab";
    case "en":
      return "en";
    default:
      return "en";
  }
}

export type Civil = { year: number; month: number; day: number };

export type WallClock = Civil & { hour: number; minute: number };

export function dayNumberOf(year: number, month: number, day: number): number {
  return Math.floor(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

function noonlessUtc(day: number): Date {
  return new Date(day * MS_PER_DAY);
}

export function gregorianOf(day: number): Civil {
  const date = noonlessUtc(day);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

export function weekdayOf(day: number): number {
  return (((day + 4) % 7) + 7) % 7;
}

const partsFormatters = new Map<CalendarId, Intl.DateTimeFormat>();

function partsFormatter(calendar: CalendarId): Intl.DateTimeFormat {
  const existing = partsFormatters.get(calendar);
  if (existing !== undefined) return existing;
  const made = new Intl.DateTimeFormat(`en-u-ca-${calendar}-nu-latn`, {
    timeZone: "UTC",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
  partsFormatters.set(calendar, made);
  return made;
}

function numberPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  const found = parts.find((part) => part.type === type);
  if (found === undefined) return 0;

  return Number.parseInt(found.value.replace(/\D/gu, ""), 10);
}

export function civilOf(day: number, calendar: CalendarId): Civil {
  if (calendar === "gregory") return gregorianOf(day);
  const parts = partsFormatter(calendar).formatToParts(noonlessUtc(day));
  return {
    year: numberPart(parts, "year"),
    month: numberPart(parts, "month"),
    day: numberPart(parts, "day"),
  };
}

export function startOfMonth(day: number, calendar: CalendarId): number {
  return day - (civilOf(day, calendar).day - 1);
}

export function monthLength(first: number, calendar: CalendarId): number {
  const { month } = civilOf(first, calendar);
  for (let length = 28; length < 32; length += 1) {
    if (civilOf(first + length, calendar).month !== month) return length;
  }
  return 31;
}

export function addMonths(first: number, delta: number, calendar: CalendarId): number {
  let cursor = first;
  for (let step = 0; step < Math.abs(delta); step += 1) {
    cursor =
      delta > 0
        ? startOfMonth(cursor + monthLength(cursor, calendar), calendar)
        : startOfMonth(cursor - 1, calendar);
  }
  return cursor;
}

export function monthGrid(first: number, weekStart: number): number[] {
  const lead = (((weekdayOf(first) - weekStart) % 7) + 7) % 7;
  const start = first - lead;
  return Array.from({ length: 42 }, (_unused, index) => start + index);
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  const existing = zoneFormatters.get(timeZone);
  if (existing !== undefined) return existing;
  const made = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  zoneFormatters.set(timeZone, made);
  return made;
}

type Full = WallClock & { second: number };

function fullClockIn(date: Date, timeZone: string): Full {
  const parts = zoneFormatter(timeZone).formatToParts(date);

  const hour = numberPart(parts, "hour");
  return {
    year: numberPart(parts, "year"),
    month: numberPart(parts, "month"),
    day: numberPart(parts, "day"),
    hour: hour === 24 ? 0 : hour,
    minute: numberPart(parts, "minute"),
    second: numberPart(parts, "second"),
  };
}

export function wallClockIn(date: Date, timeZone: string): WallClock {
  const { year, month, day, hour, minute } = fullClockIn(date, timeZone);
  return { year, month, day, hour, minute };
}

export function offsetAt(date: Date, timeZone: string): number {
  const wall = fullClockIn(date, timeZone);
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  return asUtc - date.getTime();
}

export function instantOf(wall: WallClock, timeZone: string): Date {
  const naive = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const guessed = offsetAt(new Date(naive), timeZone);
  const candidate = new Date(naive - guessed);
  const settled = offsetAt(candidate, timeZone);
  return settled === guessed ? candidate : new Date(naive - settled);
}

export function addMinutes(wall: WallClock, minutes: number, timeZone: string): WallClock {
  return wallClockIn(
    new Date(instantOf(wall, timeZone).getTime() + minutes * MS_PER_MINUTE),
    timeZone,
  );
}

export function dayNumberOfWall(wall: Civil): number {
  return dayNumberOf(wall.year, wall.month, wall.day);
}

export function isKnownZone(timeZone: string): boolean {
  if (timeZone === "") return false;
  try {
    const probe = new Intl.DateTimeFormat("en-US", { timeZone });
    return probe.resolvedOptions().timeZone !== "";
  } catch {
    return false;
  }
}

export function detectedZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone === "" ? "UTC" : zone;
  } catch {
    return "UTC";
  }
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function offsetLabel(timeZone: string, at: Date = new Date()): string {
  const minutes = Math.round(offsetAt(at, timeZone) / MS_PER_MINUTE);
  const sign = minutes < 0 ? "-" : "+";
  const absolute = Math.abs(minutes);
  return `UTC${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
}

export function zoneCity(timeZone: string): string {
  const last = timeZone.split("/").at(-1) ?? timeZone;
  return last.replaceAll("_", " ");
}

export function zoneName(timeZone: string, locale: Locale, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat(intlTag(locale), {
      timeZone,
      timeZoneName: "long",
    }).formatToParts(at);
    const named = parts.find((part) => part.type === "timeZoneName");
    if (named !== undefined && named.value !== "") return named.value;
  } catch (error) {
    console.error("time zone name lookup failed:", error);
  }
  return zoneCity(timeZone);
}

const FALLBACK_ZONES = [
  "UTC",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Asia/Tehran",
  "Asia/Baghdad",
  "Asia/Kuwait",
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Kabul",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Africa/Cairo",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Australia/Sydney",
];

export function knownZones(): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;
  if (typeof supported === "function") {
    try {
      const zones = supported("timeZone");
      if (zones.length > 0) return zones;
    } catch (error) {
      console.error("time zone list lookup failed:", error);
    }
  }
  return FALLBACK_ZONES;
}

export function formatDay(
  day: number,
  locale: Locale,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(intlTag(locale), { ...options, timeZone: "UTC" }).format(
    noonlessUtc(day),
  );
}

export function formatInstant(
  date: Date,
  locale: Locale,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat(intlTag(locale), { ...options, timeZone }).format(date);
}

export function formatNumber(value: number, locale: Locale, digits = 1): string {
  return new Intl.NumberFormat(intlTag(locale), {
    minimumIntegerDigits: digits,
    useGrouping: false,
  }).format(value);
}

const HEADING_WIDTH: Record<Locale, "short" | "narrow"> = {
  en: "short",
  fa: "narrow",
  ar: "narrow",
};

export function weekdayNames(locale: Locale, weekStart: number): string[] {
  const sunday = 3;
  return Array.from({ length: 7 }, (_unused, index) =>
    formatDay(sunday + ((weekStart + index) % 7), locale, { weekday: HEADING_WIDTH[locale] }),
  );
}

export function monthLabel(first: number, locale: Locale): string {
  return formatDay(first, locale, { month: "long", year: "numeric" });
}
